// Short display names for the ribbon and other tight spots. Full names stay in tooltips.
//
//   "Houthis (Ansar Allah)"                 → "Houthis"     (parenthetical alias dropped)
//   "Democratic Forces … Rwanda (FDLR)"     → "FDLR"        (the source's own acronym)
//   "Popular Mobilization Forces"           → "PMF"         (initials of capitalised words)
//   "United States of America" (a state)    → "United States"

/** Names longer than this (in characters) get shortened. */
const SHORT_MAX = 13;
const ACRONYM_MIN = 2;
const ACRONYM_MAX = 6;
const ALL_CAPS = /^[A-Z][A-Z0-9-]+$/;
const PARENS = /\s*\(([^)]*)\)/g;
const SLASH_PAIR = /\s+\/\s+/;
const SMALL_WORDS = new Set(['of', 'the', 'for', 'and', 'in', 'de', 'al']);

/** States whose official long form has a standard short one. */
const STATE_SHORT: Record<string, string> = {
  'United States of America': 'United States',
  'United Arab Emirates': 'UAE',
  'Democratic Republic of the Congo': 'DR Congo',
};

/** The acronym a source puts in parentheses, e.g. "(FDLR)". */
function givenAcronym(name: string): string | null {
  for (const m of name.matchAll(PARENS)) {
    const inner = m[1].trim();
    if (ALL_CAPS.test(inner) && inner.length >= ACRONYM_MIN && inner.length <= ACRONYM_MAX) {
      return inner;
    }
  }
  return null;
}

/** Initials of the words, only when every content word is capitalised (else null). */
function initials(name: string): string | null {
  const words = name
    .split(/[\s–-]+/)
    .filter((w) => w !== '' && !SMALL_WORDS.has(w.toLowerCase()));
  if (words.length < ACRONYM_MIN || words.length > ACRONYM_MAX) {
    return null;
  }
  if (!words.every((w) => /^\p{Lu}/u.test(w))) {
    return null;
  }
  return words.map((w) => w[0]).join('');
}

export function shortName(name: string, kind: string): string {
  if (kind === 'country') {
    return STATE_SHORT[name] ?? name;
  }

  const acronym = givenAcronym(name);
  if (acronym) {
    return acronym;
  }

  const plain = name.replace(PARENS, '').split(SLASH_PAIR)[0].trim();
  if (plain.length <= SHORT_MAX) {
    return plain;
  }
  return initials(plain) ?? plain;
}
